import { Fit, Layout, Rive } from '@rive-app/canvas'
import { createSignal, onCleanup, onMount, Show } from 'solid-js'
import { Motion, Presence } from 'solid-motionone'
import { loadingRewardRive } from '~/assets/anim/rive-files'
import { m } from '~/paraglide/messages'
import { rewardFlow, type Item } from '../store'
import { Indicator } from './indicator'

type Props = {
  item: Item
  items: Item[]
  currentIndex: number
}

export function MainContent(props: Props) {
  const [btnVisible, setBtnVisible] = createSignal(false)
  let clickLock = false
  let canvas!: HTMLCanvasElement // oxlint-disable-line eslint/no-unassigned-vars
  let rive: Rive | null = null

  const handleResize = () => rive?.resizeDrawingSurfaceToCanvas()

  onMount(() => {
    const t = window.setTimeout(() => setBtnVisible(true), 500)
    onCleanup(() => clearTimeout(t))

    let disposed = false
    loadingRewardRive.then((riveFile) => {
      if (disposed) return
      rive = new Rive({
        riveFile,
        canvas,
        autoplay: true,
        artboard: 'reward_reveal',
        stateMachines: ['State Machine 1'],
        layout: new Layout({ fit: Fit.FitHeight }),
        onLoad: () => {
          rive!.resizeDrawingSurfaceToCanvas()
          window.addEventListener('resize', handleResize)
          const inputs = rive!.stateMachineInputs('State Machine 1')
          for (const input of inputs) {
            if (input.name === 'status') input.value = props.item.bgAnimType
            if (input.name === 'repeat') input.value = false
          }
        },
      })
    })

    onCleanup(() => {
      disposed = true
      window.removeEventListener('resize', handleResize)
      rive?.cleanup()
      rive = null
    })
  })

  const onContinue = () => {
    if (clickLock) return
    clickLock = true
    rewardFlow.next()
  }

  return (
    <Motion.div
      class="flex flex-1 flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div class="flex flex-1 flex-col gap-6 px-6 pt-[120px]">
        <h1 class="whitespace-pre-line text-center text-[26px] font-bold text-white">
          {m.claimed_title()}
        </h1>

        <div class="relative mx-auto flex h-[40vh] w-full max-w-[40vh] items-center justify-center">
          <canvas ref={canvas} class="h-full w-full" />
          <img
            src={props.item.icon}
            class="absolute inset-0 m-auto size-[50%]"
            alt=""
          />
        </div>

        <h2
          class="text-center text-[30px] font-bold text-white"
          style={{ 'text-shadow': '0 0 15px rgba(255, 255, 255, 0.70)' }}
        >
          {props.item.title}
        </h2>

        <Indicator items={props.items} currentIndex={props.currentIndex} />
      </div>

      <Presence>
        <Show when={btnVisible()}>
          <Motion.button
            class="absolute inset-0 flex items-end justify-center pb-12 text-sm text-white"
            onClick={onContinue}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <span class="grid h-[120px] animate-pulse place-items-center">
              {m.claimed_next()}
            </span>
          </Motion.button>
        </Show>
      </Presence>
    </Motion.div>
  )
}
